import { HarnessBackground, SectionLengthStatus } from '../types';

/** Longest-edge cap (px) applied when a background image is imported. */
export const BACKGROUND_MAX_EDGE = 3000;
/** WebP quality used when re-encoding an imported background image. */
export const BACKGROUND_WEBP_QUALITY = 0.85;
/** Opacity a freshly added background starts at, so the diagram stays legible. */
export const BACKGROUND_DEFAULT_OPACITY = 0.5;

/** Placement of the background image in world space. */
export interface BackgroundTransform {
  x: number;
  y: number;
  scale: number;
}

/** A point in world coordinates. */
export interface WorldPoint {
  x: number;
  y: number;
}

/**
 * Scales dimensions down to fit within a square of `maxEdge`, preserving aspect
 * ratio. Images already within the bound are returned unchanged (never upscaled).
 */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return { width, height };
  }
  const factor = maxEdge / longest;
  return { width: Math.round(width * factor), height: Math.round(height * factor) };
}

/**
 * Re-scales the background so the two given world points, which mark a feature of
 * known real length, end up `realMm` apart, anchoring point `a` in place. This
 * calibrates one image pixel to one world unit (mm), keeping segment lengths and
 * the traced layout in the same units. Returns the transform unchanged when the
 * two points coincide or the real length is not positive.
 */
export function calibrateScale(
  transform: BackgroundTransform,
  a: WorldPoint,
  b: WorldPoint,
  realMm: number
): BackgroundTransform {
  const worldDistance = Math.hypot(b.x - a.x, b.y - a.y);
  if (worldDistance === 0 || realMm <= 0) {
    return transform;
  }
  const newScale = transform.scale * (realMm / worldDistance);
  // Image-pixel coordinate of the anchor point, kept fixed across the rescale.
  const anchorImageX = (a.x - transform.x) / transform.scale;
  const anchorImageY = (a.y - transform.y) / transform.scale;
  return {
    scale: newScale,
    x: a.x - anchorImageX * newScale,
    y: a.y - anchorImageY * newScale
  };
}

/**
 * Base tolerance (mm) within which a section length still counts as matching the
 * layout, widened by 1% of the distance so long runs are not flagged over
 * sub-millimetre rounding or a tiny connector nudge.
 */
export const LAYOUT_LENGTH_TOLERANCE_MM = 2;

/** Whether a stored section length matches the layout-calculated distance. */
export function lengthMatchesLayout(storedMm: number, calculatedMm: number): boolean {
  const tolerance = Math.max(LAYOUT_LENGTH_TOLERANCE_MM, calculatedMm * 0.01);
  return Math.abs(storedMm - calculatedMm) <= tolerance;
}

/**
 * Classifies a section's measured length against its layout distance: 'auto' when
 * they match, 'overwritten' when they diverge. Returns undefined when there is no
 * layout distance to compare against or the section is not measured yet.
 */
export function sectionLengthStatus(
  storedMm: number,
  calculatedMm: number | null
): SectionLengthStatus | undefined {
  if (calculatedMm === null || calculatedMm <= 0 || storedMm <= 0) {
    return undefined;
  }
  return lengthMatchesLayout(storedMm, calculatedMm) ? 'auto' : 'overwritten';
}

/**
 * Builds a background placed so its centre sits at the world origin at 1:1 scale.
 * Callers calibrate and reposition it afterwards.
 */
export function createBackground(src: string, imageWidth: number, imageHeight: number): HarnessBackground {
  return {
    src,
    imageWidth,
    imageHeight,
    x: -imageWidth / 2,
    y: -imageHeight / 2,
    scale: 1,
    opacity: BACKGROUND_DEFAULT_OPACITY,
    locked: false
  };
}
